import React, { createContext, useContext, useReducer } from 'react'
import { useAccountTypes } from '../../../AccountTypeContext'
import { loadStkData, StkDataObj } from '../../../Data'
import { useHostServices } from '../../../HostServices'
import { LoadingState } from './types'
import { StkWidgetActionType, StkWidgetReducer } from './StkWidgetReducer'

export type WidgetContextProps = {
  activeAccountId: string
  loadingState: LoadingState
  errorMsg: string
  setActiveAccountId: (accountId: string) => void
  stkSummaryData: StkDataObj | null
}

export const WidgetContext = createContext<WidgetContextProps>(
  (null as unknown) as WidgetContextProps,
)

export const useWidget = (): WidgetContextProps => useContext(WidgetContext)

export const WidgetProvider: React.FC = ({ children }) => {
  const { stkAccounts } = useAccountTypes()
  const { session } = useHostServices()

  const initialState = {
    loadingState: LoadingState.UNLOADED,
    stkSummaryData: null,
    activeAccountId: stkAccounts[0]?.id,
    errorMsg: '',
  }

  const [state, dispatch] = useReducer(StkWidgetReducer, initialState)

  React.useEffect(() => {
    dispatch({ type: StkWidgetActionType.LOADING })

    // istanbul ignore next
    loadStkData({ accountId: state.activeAccountId, session })
      .then((data: StkDataObj) => {
        dispatch({ type: StkWidgetActionType.SUCCESS, payload: data })
      })
      .catch(() => {
        dispatch({ type: StkWidgetActionType.ERROR })
      })
  }, [state.activeAccountId, session])

  function setActiveAccountId(accountId: string): void {
    dispatch({
      type: StkWidgetActionType.SET_ACTIVE_ACCOUNT_ID,
      value: accountId,
    })
  }

  return (
    <WidgetContext.Provider
      value={{
        activeAccountId: state.activeAccountId,
        loadingState: state.loadingState,
        errorMsg: state.errorMsg,
        setActiveAccountId,
        stkSummaryData: state.stkSummaryData,
      }}
    >
      {children}
    </WidgetContext.Provider>
  )
}
